import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../admin/AuthContext';
import { adminApi } from '../admin/api';
import { STEPS, StepBody } from './steps';

function tenantToForm(t) {
  const s = t.settings || {};
  const c = s.contact || {};
  const m = s.map || {};
  const ci = s.checkin || {};
  const w = s.wifi || {};
  return {
    brandName: t.brand?.name || '',
    payoff: (t.brand?.payoff && typeof t.brand.payoff === 'object' ? t.brand.payoff.it : t.brand?.payoff) || '',
    logo_url: t.logo_url || null,
    theme: { bg: '#F7F4F0', bg2: '#E9E2D7', accent: '#DCC6B7', brown: '#8B6F4E', ink: '#2B282B', ...(t.theme || {}) },
    address: c.address || '',
    phone: t.contacts?.phone || c.phone || '',
    email: t.contacts?.email || c.email || '',
    website: c.website || '',
    lat: m.lat ?? '',
    lng: m.lng ?? '',
    zoom: m.zoom ?? 13,
    place_id: m.place_id || '',
    checkin_time: ci.checkin_time || '15:00',
    checkout_time: ci.checkout_time || '10:00',
    access_code: ci.access_code || '',
    checkin_instructions: (ci.instructions && ci.instructions.it) || '',
    wifi_ssid: w.ssid || '',
    wifi_password: w.password || '',
    gallery: s.gallery || [],
  };
}

function buildPatch(form, baseSettings, stepIndex, completed) {
  return {
    brand: { name: form.brandName || 'La mia struttura', payoff: { it: form.payoff || '' } },
    logo_url: form.logo_url,
    theme: form.theme,
    contacts: { phone: form.phone, email: form.email },
    settings: {
      ...baseSettings,
      contact: { address: form.address, phone: form.phone, email: form.email, website: form.website },
      map: { lat: parseFloat(form.lat) || 0, lng: parseFloat(form.lng) || 0,
             zoom: parseInt(form.zoom, 10) || 13, place_id: form.place_id, marker: true },
      checkin: { checkin_time: form.checkin_time, checkout_time: form.checkout_time,
                 access_code: form.access_code, instructions: { it: form.checkin_instructions } },
      wifi: { ssid: form.wifi_ssid, password: form.wifi_password },
      gallery: form.gallery,
      onboarding: { step: stepIndex, completed },
    },
  };
}

export function Wizard() {
  const { tenant, tenantId, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!tenant || !tenantId) { navigate('/onboarding', { replace: true }); return; }
    if (tenant.published || tenant.settings?.onboarding?.completed) { navigate('/admin', { replace: true }); return; }
    setForm(tenantToForm(tenant));
    setStep(tenant.settings?.onboarding?.step || 0);
  }, [loading, tenant, tenantId, navigate]);

  const set = useCallback((patch) => setForm((f) => ({ ...f, ...patch })), []);

  const save = useCallback(async (targetStep, completed = false, published = false) => {
    setSaving(true);
    try {
      const body = buildPatch(form, tenant.settings || {}, targetStep, completed);
      if (published) body.published = true;
      await adminApi.updateTenant(tenantId, body);
    } finally { setSaving(false); }
  }, [form, tenant, tenantId]);

  if (!form) {
    return <div className="flex min-h-screen items-center justify-center bg-paelbi-bg text-paelbi-ink/50">Caricamento...</div>;
  }

  const isLast = step === STEPS.length - 1;
  const meta = STEPS[step];
  const pct = Math.round(((step + 1) / STEPS.length) * 100);

  const next = async () => {
    if (isLast) {
      await save(step, true, true);
      await refresh();
      navigate('/admin', { replace: true });
      return;
    }
    const t = step + 1;
    await save(t);
    setStep(t);
  };
  const back = async () => {
    if (step === 0) return;
    const t = step - 1;
    await save(t);
    setStep(t);
  };

  return (
    <div className="min-h-screen bg-paelbi-bg px-4 py-8" data-testid="onboarding-wizard">
      <div className="mx-auto max-w-xl">
        <div className="mb-6">
          <div className="mb-2 flex items-center justify-between text-xs text-paelbi-ink/60">
            <span>Passo {step + 1} di {STEPS.length}</span>
            <span>{pct}%</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-paelbi-bg2">
            <div className="h-full rounded-full bg-paelbi-brown transition-all duration-300" style={{ width: `${pct}%` }} />
          </div>
        </div>

        <div className="rounded-2xl border border-paelbi-line bg-white p-6 shadow-card sm:p-8">
          <h1 className="font-display text-2xl text-paelbi-ink" data-testid="wizard-step-title">{meta.title}</h1>
          <p className="mb-6 mt-1 text-sm text-paelbi-ink/60">{meta.subtitle}</p>

          <StepBody stepKey={meta.key} form={form} set={set} tenantSlug={tenant.slug} />

          <div className="mt-8 flex items-center justify-between gap-3">
            <button type="button" onClick={back} disabled={step === 0 || saving}
              className="rounded-btn border border-paelbi-line px-5 py-2.5 text-sm font-semibold text-paelbi-ink disabled:opacity-40"
              data-testid="wizard-back">Indietro</button>
            <button type="button" onClick={next} disabled={saving}
              className="rounded-btn bg-paelbi-brown px-6 py-2.5 text-sm font-semibold text-white hover:bg-paelbi-brown-dark disabled:opacity-60"
              data-testid="wizard-next">
              {saving ? 'Salvataggio...' : isLast ? 'Pubblica e vai alla dashboard' : 'Avanti'}
            </button>
          </div>
        </div>
        <p className="mt-4 text-center text-xs text-paelbi-ink/50">
          Le modifiche vengono salvate automaticamente. Puoi uscire e riprendere quando vuoi.
        </p>
      </div>
    </div>
  );
}

export default Wizard;
